import {Trans} from '@lingui/macro';
import tagifyCss from '@yaireo/tagify/dist/tagify.css';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useCss} from '../../../../libs/shared/ui/src/css/cssContext';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {OauthClientModel} from '../../../models/api/oauthClientModel';
import {SettingsResourcePagesEnum} from '../../../models/url/settingsUrlModel';
import {SettingsFooter} from '../../common/settingsWrapper';
import {StyledEditorMain, StyledEditorWrapper} from '../../editors/commons/editorCommons';
import {OauthClientCredentialsCard} from './cards/oauthClientCredentialsCard';
import {OauthClientRedirectUrlsCard} from './cards/oauthClientRedirectUrlsCard';

/*
 * Props.
 */

interface OauthClientDetailsProps {
  application?: LocalApplicationModel;
  oauthClient?: OauthClientModel;
  navigateTo: (resourcePage: SettingsResourcePagesEnum, id?: number) => void;
}

/*
 * Style.
 */

const StyledDetailsWrapperDiv = styled.div`
  padding: 0 48px;
`;

const StyledDescriptionDiv = styled.div`
  font-size: ${fontSizes.large};
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.medium};
  margin-bottom: 8px;
`;

const StyledButtonsDiv = styled.div`
  display: flex;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 10px;
`;

/*
 * Component.
 */

export const OauthClientDetails: FC<OauthClientDetailsProps> = (props) => {
  const {application, oauthClient, navigateTo} = props;

  // Enable tagify styles.
  useCss(tagifyCss);

  return (
    <>
      <StyledEditorMain>
        <StyledEditorWrapper>
          {!oauthClient && renderOverview(navigateTo)}
          {renderClientDetails(application, oauthClient)}
        </StyledEditorWrapper>
      </StyledEditorMain>
      {oauthClient && renderFooter(navigateTo)}
    </>
  );
};

/*
 * Helpers.
 */

function renderOverview(navigateTo: OauthClientDetailsProps['navigateTo']) {
  return (
    <StyledDetailsWrapperDiv>
      <StyledDescriptionDiv>
        <Trans id="oauth-client-details-title">This application does not have an OAuth client yet.</Trans>
      </StyledDescriptionDiv>
      <StyledButtonsDiv>
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          size={VisualSizesEnum.MEDIUM}
          onClick={() => navigateTo(SettingsResourcePagesEnum.NEW)}
        >
          <Trans id="oauth-client-new">Create OAuth client</Trans>
        </Button>
      </StyledButtonsDiv>
    </StyledDetailsWrapperDiv>
  );
}

function renderClientDetails(
  application: LocalApplicationModel | undefined,
  oauthClient: OauthClientModel | undefined,
) {
  if (oauthClient) {
    return (
      <>
        <OauthClientCredentialsCard application={application} clientId={oauthClient.clientId} />

        <OauthClientRedirectUrlsCard redirectUrls={oauthClient.redirectUrls} onUpdate={() => {}} isReadOnly />
      </>
    );
  }

  return null;
}

function renderFooter(navigateTo: OauthClientDetailsProps['navigateTo']) {
  return (
    <SettingsFooter>
      <Button variant={ButtonVariantsEnum.PRIMARY} onClick={() => navigateTo(SettingsResourcePagesEnum.EDIT)}>
        <Trans id="oauth-client-edit">Edit OAuth client</Trans>
      </Button>
    </SettingsFooter>
  );
}
